import { ReactElement } from "react";
import { Button } from "@twilio-paste/core/button";
import { Text } from "@twilio-paste/core/text";
import { Anchor } from "@twilio-paste/core/anchor";
import { LinkExternalIcon } from "@twilio-paste/icons/esm/LinkExternalIcon";
import { bodyStyles } from "../components/styles/MessageBubble.styles";
import "./parsedMessages.module.css";

const BOT_AUTHOR = "Dentalpro";

function checkIfAuthorIsBot(author: string): boolean {
  return author === BOT_AUTHOR;
}

function checkChiamaString(s: string): boolean {
  const pattern = /^Chiama (\+?\d{1,3}\s?\d+)$/;
  return pattern.test(s);
}

function checkPrenotaVisita(s: string): boolean {
  const pattern = /^Prenota una visita (https?:\/\/[^\s]+|www\.[^\s]+)$/;
  return pattern.test(s);
}

function checkScopriAltriCentri(s: string): boolean {
  const pattern = /^Scopri altri centri (https?:\/\/[^\s]+|www\.[^\s]+)$/;
  return pattern.test(s);
}

function checkChiediInformazioni(s: string): boolean {
  return s === "Chiedi informazioni";
}

const protocol = "((?:http|https|rtsp):\\/\\/)";
const domainAndSubdomains = "(?:(?:[a-zA-Z0-9][-]*)*[a-zA-Z0-9]\\.)+";
const genericTopLevelDomains =
  "aero|arpa|asia|app|" +
  "biz|buzz|" +
  "cat|com|coop|credit|" +
  "edu|" +
  "gov|" +
  "info|int|" +
  "jobs|" +
  "mil|mobi|museum|" +
  "name|net|" +
  "org|" +
  "pro|" +
  "tel|travel";
const countryTopLevelDomains =
  "a[cdefgilmnoqrstuwxz]|" +
  "b[abdefghijlmnoqrstvwyz]|" +
  "c[acdfghiklmnoruvwxyz]|" +
  "d[ejkmoz]|" +
  "e[ceghrstu]|" +
  "f[ijkmor]|" +
  "g[abdefghilmnpqrstuwy]|" +
  "h[kmnrtu]|" +
  "i[delmnoqrst]|" +
  "j[emop]|" +
  "k[eghimnprwyz]|" +
  "l[abcikrstuvy]|" +
  "m[acdefghklmnopqrstuvwxyz]|" +
  "n[acefgilopruz]|" +
  "om|" +
  "p[aefghklmnrstwy]|" +
  "qa|" +
  "r[eosuw]|" +
  "s[abcdeghijklmnorstuvxyz]|" +
  "t[cdfghjklmnoprtvwz]|" +
  "u[agkmsyz]|" +
  "v[aceginu]|" +
  "w[fs]|" +
  "y[etu]|" +
  "z[amw]";
const port = "(?:\\:\\d{1,5})?";
const path = '(?:[/?#][^\\s"]*)?';
const startOfMatchedUrl = "(?:\\b|^)";
const endOfMatchedUrl = "(?:\\b|$)";

// only captured group is the protocol, e.g. https://
const urlRegexString = `${startOfMatchedUrl}(?:${protocol}\\S+|(?:${protocol})?${domainAndSubdomains}(?:${genericTopLevelDomains}|${countryTopLevelDomains})${port}${path})${endOfMatchedUrl}`;
const urlRegex = new RegExp(urlRegexString, "gi");

export const parseMessageBody = (
  body: string,
  belongToCurrentUser: boolean,
  callChiediInformazioni: () => void,
  author: string
) => {
  const renderedResult: Array<ReactElement | string> = [];

  const lineBreakRegexp = new RegExp(/[\n\r]/);
  const lines = body.split(lineBreakRegexp);
  lines.forEach((line) => {
    let index = 0;
    let regexResult;

    const isBotAuthor = checkIfAuthorIsBot(author);

    if (checkChiediInformazioni(line) && isBotAuthor) {
      renderedResult.push(
        <Button
          variant="primary"
          key={index}
          onClick={() => callChiediInformazioni()}
        >
          Chiedi informazioni
        </Button>
      );
    } else if (checkScopriAltriCentri(line) && isBotAuthor) {
      const match = line.match(
        /^Scopri altri centri (https?:\/\/[^\s]+|www\.[^\s]+)$/
      );
      if (match && match[1]) {
        const link = match[1];
        renderedResult.push(
          <Anchor key={index} href={link} data-paste-anchor="anchor-button">
            <LinkExternalIcon decorative={true} />
            Scopri Altri Centri
          </Anchor>
        );
      }
    } else if (checkPrenotaVisita(line) && isBotAuthor) {
      const match = line.match(
        /^Prenota una visita (https?:\/\/[^\s]+|www\.[^\s]+)$/
      );
      if (match && match[1]) {
        const link = match[1];
        renderedResult.push(
          <Anchor key={index} href={link} data-paste-anchor="anchor-button">
            <LinkExternalIcon decorative={true} />
            Prenota visita
          </Anchor>
        );
      }
    } else if (checkChiamaString(line) && isBotAuthor) {
      const match = line.match(/^Chiama (\+?\d{1,3}\s?\d+)$/);
      if (match && match[1]) {
        const numero = match[1];
        renderedResult.push(
          <Anchor
            key={index}
            href={`tel:${numero}`}
            data-paste-anchor="anchor-button"
          >
            <LinkExternalIcon decorative={true} />
            Chiama {numero}
          </Anchor>
        );
      }
    } else if (index < line.length) {
      renderedResult.push(
        <Text {...bodyStyles} as="p" key={index}>
          {line.substring(index, line.length)}
        </Text>
      );
    } else {
      while ((regexResult = urlRegex.exec(line))) {
        // Add regular body text before the URL
        renderedResult.push(
          <Text {...bodyStyles} as="p" key={index}>
            {line.substring(index, regexResult.index)}
          </Text>
        );

        const [url, urlProtocol] = regexResult;
        const urlHref = urlProtocol ? url : `http://${url}`;
        renderedResult.push(
          <Anchor
            href={urlHref}
            key={index}
            variant={belongToCurrentUser ? "inverse" : "default"}
          >
            {url}
          </Anchor>
        );
        index = regexResult.index + regexResult[0].length;
      }
    }

    renderedResult.push("\n");
  });

  // Remove the newline added after the last parsed line
  renderedResult.pop();

  return renderedResult;
};
